import { useCallback } from 'react';

import { useFormContext } from 'react-hook-form';

import { WIDGET_MESSAGE } from '@hospitality-consumer-facing/lsk-reservation/shared';

import { ReservationFormData } from '../../reservation/types';
import { BusinessLocation, ReservationConfirmation } from '../../shared/types';
import { useGetReservation, useCancelReservation } from '../../status/hooks';
import { useWidgetCommunicationContext } from '../providers/widget-communication.provider';

export function useReservationConfirmationData({
  businessLocation,
  reservationConfirmation,
}: {
  businessLocation: BusinessLocation;
  reservationConfirmation: ReservationConfirmation;
}) {
  const { sendMessage } = useWidgetCommunicationContext();

  const form = useFormContext<ReservationFormData>();
  const partySize = form.getValues('partySize');
  const utcScheduledAt = form.getValues('utcScheduledAt');

  const {
    data: reservation,
    isLoading: isReservationLoading,
    isError: isReservationError,
  } = useGetReservation({
    restaurantId: businessLocation.restaurantId,
    reservationId: reservationConfirmation.reservationId,
    secret: reservationConfirmation.secret,
  });

  const { mutateAsync, isPending: isCancelling } = useCancelReservation({
    restaurantId: businessLocation.restaurantId,
    reservationId: reservationConfirmation.reservationId,
    secret: reservationConfirmation.secret,
  });

  const cancelReservation = useCallback(async () => {
    await mutateAsync();
    sendMessage({
      type: WIDGET_MESSAGE.BOOKING_CANCELED,
      payload: {
        reservationId: reservationConfirmation.reservationId,
        partySize: reservation?.partySize,
        utcScheduledAt: reservation?.utcScheduledAt,
      },
    });
  }, [mutateAsync, reservation, reservationConfirmation, sendMessage]);

  return {
    partySize,
    utcScheduledAt,
    reservation,
    isReservationLoading,
    isReservationError,
    cancelReservation,
    isCancelling,
  };
}
